import { Component, Output } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import * as _ from 'lodash';
import { Observable, combineLatest, concatMap, filter, map, tap } from 'rxjs';
import { CuttingPanelBaseSlot, GrooveTypeEnum } from '../../../models/cutting/cutting-panel-base-slot.model';
import { PlateTypeEnum } from '../../../models/cutting/cutting-panel-outline.model';

import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import {
  Chamfer2SlotParams,
  ChamferSlotParams,
  CountertopSlot,
  FilletSlotParams,
  InPlateSlotParams,
  InSideSlotParams,
  RabbetSlotParams,
} from '../../../models/cutting/countertop/outline';
import { CuttingCustomGrooveSchema } from '../../../models/cutting/cutting-custom-groove-schema.model';
import { Countertop } from '../../../models/cutting/public-api';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import { CountertopSettingsDataService } from '../countertop-settings-data.service';
import { CountertopSettingsStateService } from '../countertop-settings-state.service';
import {
  SlotWizardDialogComponent,
  SlotWizardDialogData,
  SlotWizardDialogResult,
} from '../slot-wizard-dialog/slot-wizard-dialog.component';

export class SlotsBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SlotsMixinBase = mixinDisabled(SlotsBase);

@Component({
  selector: 'csl-slots',
  templateUrl: './slots.component.html',
  styles: [
    `
      :host {
        @apply flex flex-col;
      }

      .mat-column-options {
        width: 40px;
      }
    `,
  ],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    MatDialogModule,
    MatLegacyTableModule,
    MatLegacyButtonModule,
    MatIconModule,
    MatLegacySelectModule,
    MatLegacyFormFieldModule,
    MatDividerModule,
    MatLegacyTooltipModule,
    NgClass,
  ],
})
export class SlotsComponent extends _SlotsMixinBase implements CanDisable {
  countertop: Countertop;
  slots: CountertopSlot[];

  action$: Observable<any>;
  data$: Observable<any>;

  selection = new SelectionModel<CountertopSlot>(false);

  @Output() selectionChange = this.selection.changed;

  constructor(
    private countertopSettingsService: CountertopSettingsStateService,
    private dialogs: LegacyDialogsService,
    private countertopSettingsDataService: CountertopSettingsDataService
  ) {
    super();

    this.data$ = this.countertopSettingsService.countertop$.pipe(
      tap((p: Countertop) => {
        this.countertop = p;
        this.slots = _.map(this.countertop?.outline?.slots, value => value).flat();
      })
    );
  }

  addSlot(): void {
    this.action$ = combineLatest([
      this.countertopSettingsDataService.getAvailableSlots(),
      this.getCustomSlotsSchemas(),
    ]).pipe(
      concatMap(([slots, schemas]) =>
        this.dialogs.component<SlotWizardDialogData, SlotWizardDialogResult>(SlotWizardDialogComponent, {
          countertop: this.countertop,
          slots,
          deleteCustomSlot: this.countertopSettingsDataService.deleteCustomSlot.bind(
            this.countertopSettingsDataService
          ),
          postCustomSlotsData: this.countertopSettingsDataService.addCustomSlot.bind(
            this.countertopSettingsDataService
          ),
          customSlotSchemas: schemas,
        })
      ),
      filter(r => !!r),
      tap((s: SlotWizardDialogResult) => {
        s.sideNumbers.forEach(n => this.countertop.outline.slots[n].push(this.convertToCountertopSlot(+n, s.slot)));

        this.countertopSettingsService.updateCountertop(this.countertop);
      })
    );
  }

  getCustomSlotsSchemas(): Observable<CuttingCustomGrooveSchema[]> {
    return combineLatest([
      this.countertopSettingsDataService.getCustomSlotSchemas(null),
      this.countertopSettingsDataService.getCustomSlotOption(),
    ]).pipe(
      map(([schemas, o]) => {
        const typeAvailability = {
          [GrooveTypeEnum.InPlate]: o.inPlate,
          [GrooveTypeEnum.InSide]: o.inSide,
          [GrooveTypeEnum.Rabbet]: o.rabbet,
          [GrooveTypeEnum.Fillet]: o.fillet,
          [GrooveTypeEnum.Chamfer]: o.chamfer,
          [GrooveTypeEnum.Chamfer2]: o.chamfer2,
        };

        return schemas.filter(s => typeAvailability[s.type]);
      })
    );
  }

  private convertToCountertopSlot(sideNumber: number, cuttingPanelSlot: CuttingPanelBaseSlot): CountertopSlot {
    const { id, name, designation, type, custom, plate, r, fi, dx, b, h, g } = cuttingPanelSlot;

    let params:
      | FilletSlotParams
      | ChamferSlotParams
      | Chamfer2SlotParams
      | RabbetSlotParams
      | InPlateSlotParams
      | InSideSlotParams
      | undefined;

    switch (type) {
      case GrooveTypeEnum.Fillet:
        params = new FilletSlotParams();
        params.r = r;
        break;
      case GrooveTypeEnum.Chamfer:
        params = new ChamferSlotParams();
        params.fi = fi;
        break;
      case GrooveTypeEnum.Chamfer2:
        params = new Chamfer2SlotParams();
        params.dx = dx;
        params.b = b;
        break;
      case GrooveTypeEnum.Rabbet:
        params = new RabbetSlotParams();
        params.h = h;
        params.g = g;
        break;
      case GrooveTypeEnum.InPlate:
        params = new InPlateSlotParams();
        params.h = h;
        params.g = g;
        (params as InPlateSlotParams).dx = dx;
        break;
      case GrooveTypeEnum.InSide:
        params = new InSideSlotParams();
        params.h = h;
        params.g = g;
        (params as InSideSlotParams).dx = dx;
        break;
      default:
        break;
    }

    const countertopSlot = new CountertopSlot();
    countertopSlot.id = id;
    countertopSlot.name = name;
    countertopSlot.designation = designation;
    countertopSlot.type = type;
    countertopSlot.custom = custom;
    countertopSlot.plate = plate;
    countertopSlot.parameters = params;
    countertopSlot.sideNumber = sideNumber;

    return countertopSlot;
  }

  delete(item: CountertopSlot, sideName: string): void {
    _.pull(this.countertop.outline.slots[sideName], item);
    this.countertopSettingsService.updateCountertop(this.countertop);
  }

  // getDescription(slot: CuttingPanelBaseSlot): string {
  //   if (!slot) {
  //     return $localize`Выберите паз`;
  //   }

  //   return slot.designation;
  // }

  getIconLink(type: GrooveTypeEnum): string {
    if (_.isNil(type)) {
      return '';
    }

    const iconPath = (name: string): string => `./assets/images/slots/${name}.svg`;

    switch (type) {
      case GrooveTypeEnum.Chamfer:
        return iconPath('chamfer');

      case GrooveTypeEnum.Rabbet:
        return iconPath('rabbet');

      case GrooveTypeEnum.InPlate:
        return iconPath('in-plate');

      case GrooveTypeEnum.InSide:
        return iconPath('in-side');

      default:
        return '';
    }
  }

  setPlate(item: CountertopSlot, value: PlateTypeEnum): void {
    item.plate = value;

    this.countertopSettingsService.updateCountertop(this.countertop);
  }

  // isBackPlate(item: CuttingPanelBaseSlot): boolean {
  //   return item?.plate === PlateTypeEnum.Back;
  // }

  // getPlateClass(slot: CuttingPanelBaseSlot): string {
  //   if (!slot) {
  //     return '';
  //   }

  //   switch (slot.plate) {
  //     case PlateTypeEnum.Back:
  //       return 'back-plate';

  //     case PlateTypeEnum.Face:
  //       return 'face-plate';

  //     case PlateTypeEnum.Edge:
  //       return 'edge-plate';

  //     default:
  //       return '';
  //   }
  // }
}
